
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useTransition,
} from "react";
import { useFields } from "./FieldsProvider";
import { useMetaTable } from "./MetaTableProvider";
import { useMetaRequest } from "./MetaRequestProvider";
import { useFilters } from "./FiltersProvider";
import {
  createCompanionRequest,
  getOrderedCompanionsWithGuests,
  updateGuests,
} from "@/lib/api/handlers/GuestHandling";
import {
  approvalBlockedGuestIds,
  capacityContextOf,
  CapacityContext,
  enqueuedTriggersFor,
  isServerErrorResponse,
  throwServerError,
} from "@/lib/api/errors/ErrorHandler";
import { CapacityDecisionModal } from "@/components/CapacityDecisionModal/CapacityDecisionModal";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";
import type { DecideOutcome } from "./RegistrantsProvider";
import { parseFiltersApiRequest } from "@/lib/utils/parser";
import { AcceptanceState } from "../types/enums/AcceptanceState";
import { Guest } from "../types/Guest";
import { dropSelfWrittenFields } from "@/lib/guest-reconcile/selfWrittenFields";
import { readBackGuestVerdict } from "@/lib/guest-reconcile/readBackAcceptance";
import {
  armGuestReconcile,
  armCoarseReconcile,
  GuestReconciledDetail,
  FieldWatch,
} from "@/lib/guest-reconcile/reconcileScheduler";
import { applyServerTruth } from "@/lib/utils/guest-patch";
import { deriveLifecycleTriggers, explicitUpdateTrigger } from "@/lib/utils/derive-lifecycle-triggers";
import { restoreRow } from "@/lib/utils/rows";
import { COMPANION_DTO_FIELDS } from "@/lib/utils/rows";
import { useGuestAutomationGate } from "@/hooks/useGuestAutomationGate";
import { useSignatureEffect } from "../hooks/useSignatureEffect";
import { useQueryClient } from "@tanstack/react-query";
import { mergeGuestPatch } from "@/lib/utils/guest-patch";
import {
  addGuestToRequestsCache,
  applyServerTruthInRequestsCache,
  dropGuestFromRequestsCache,
  isPendingRequestRow,
  refetchRequestsSnapshots,
} from "@/lib/query/requestsCache";
import type { GuestDataUpdatedDetail } from "./GuestsProvider";
import { useToast } from "@/components/Toast/Toast";
import { useTranslations } from "next-intl";

interface CompanionsContextProps {
  companions: Guest[];
  loading: boolean;
  /** Same contract as the twin — see {@link DecideOutcome}. */
  accept: (guestId: string) => Promise<DecideOutcome>;
  decline: (guestId: string) => Promise<DecideOutcome>;
  /** Twin of RegistrantsProvider.decide. */
  decide: (
    guestId: string,
    next: AcceptanceState,
    opts?: { retriggerAutomations?: boolean },
  ) => Promise<DecideOutcome>;
  errors: string | null;
  setErrors: React.Dispatch<React.SetStateAction<string | null>>;
  setCompanions: React.Dispatch<React.SetStateAction<Guest[]>>;
  createCompanion: (companion: Guest) => Promise<void>;
}

const CompanionsContext = createContext<CompanionsContextProps | undefined>(
  undefined
);

interface CompanionsProviderProps {
  children: ReactNode;
  initialCompanions: Guest[];
  eventId: string;
}

export const CompanionsProvider = ({
  initialCompanions,
  children,
  eventId,
}: CompanionsProviderProps) => {
  const { allFieldsUnfiltered } = useFields();
  const { activeFilterState } = useFilters();
  // `isPending` (background refetch on filter/page/archived/search change) is
  // the ONLY signal that should dim the table behind a spinner. Accept/decline/
  // create are optimistic — they mutate `companions` in place — so they must
  // NOT raise the table's loading overlay, otherwise every action flashes the
  // whole table invisible for the network round-trip (looks like a reload).
  const [isPending, startTransition] = useTransition();
  const [errors, setErrors] = useState<string | null>(null);
  // Mirrors RegistrantsProvider. The capacity gate is guest-type agnostic
  // server-side, so this lane can hit the same 409 — it just had nowhere to put
  // it, and the admin's opt-in died with the error toast.
  const [capacityDecision, setCapacityDecision] = useState<{
    guestId: string;
    context: CapacityContext;
    /** Carried across the interruption so the override arm can still announce. */
    retriggerAutomations: boolean;
  } | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  /** The RAW failure of the last popup choice — shown by the popup, which
   *  stays open (M-10). Reset where the popup opens. Twin of RegistrantsProvider. */
  const [decisionError, setDecisionError] = useState<unknown>(null);
  // H29 (companion parity): surface mutation failures through the app toast
  // instead of the renderer-less `errors` state.
  const { showToast } = useToast();
  const tErr = useTranslations("errors");
  // The WHOLE error, not its `.message` — the per-row code is the reason of
  // a guest batch 400 (M-01b). Twin of RegistrantsProvider.
  const surfaceError = useCallback(
    (error: unknown) => {
      showToast(describeBatchUpdateError(tErr, error, tErr("unknown")), "error");
    },
    [showToast, tErr],
  );
  const { archived, page, pageSize, search, setTotalCount } = useMetaTable();
  // Tab-badge count lives in MetaRequestProvider (separate from the table's
  // totalCount). Accept/decline keep it in sync from TCCAction; the create
  // path must mirror that here so the badge bumps without a reload.
  const { setCounts } = useMetaRequest();
  const [companions, setCompanions] = useState<Guest[]>(initialCompanions);
  // Rollback reads from HERE, not from the `companions` closure — see the twin
  // in RegistrantsProvider for why a state-closure snapshot let a late failure
  // undo an earlier success.
  const rowsRef = useRef<Guest[]>(companions);
  rowsRef.current = companions;
  // Registrants-provider parity: reconcile when the host query refetches
  // (post-invalidation remount) — the signature effect keys on params, so the
  // stale seed would otherwise pin forever.
  // Compared by CONTENT, not identity (identity-churn pins).
  const initialSeedRef = useRef(JSON.stringify(initialCompanions));
  // The host query ALWAYS holds page 1 with the mount filters. Once the user
  // has paged, searched or refiltered here, adopting that array would swap the
  // visible rows for the wrong page — so a diverged view keeps its own rows and
  // only re-baselines the seed. (Adopting unconditionally is how a filter/view
  // save used to yank a user on page 3 back to unfiltered page-1 rows while the
  // paginator still read "Seite 3".)
  const signatureRef = useRef<string | null>(null);
  const mountSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const seedSignature = JSON.stringify(initialCompanions);
    if (initialSeedRef.current === seedSignature) return;
    initialSeedRef.current = seedSignature;
    if (signatureRef.current !== mountSignatureRef.current) return;
    setCompanions(
      initialCompanions.filter((g) => !recentlyMutatedIdsRef.current.has(g.guestId!)),
    );
  }, [initialCompanions]);
  // Registrants-provider parity: the requests-page + guestlist query snapshots
  // outlive this provider — without invalidation a remount within staleTime
  // resurrects an already-actioned companion request and invites a second,
  // contradictory write. refetchType "none": the signature refetch keeps the
  // mounted page live.
  const queryClient = useQueryClient();
  // Cheap "is an automation even armed for this trigger?" — no plugin, or no
  // UPDATE_GUEST pipe on the trigger, means we never start a reconcile wave.
  const { mayRun, watchedFieldsFor } = useGuestAutomationGate(eventId);
  const invalidateRequestCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["guestsRequests", eventId], refetchType: "none" });
    void queryClient.invalidateQueries({ queryKey: ["guestlist", eventId], refetchType: "none" });
  }, [queryClient, eventId]);
  // Holds guestIds whose accept/decline was applied optimistically. The
  // refetch effect filters its server response through this set so a
  // background refetch can't put a just-actioned row back on screen
  // before the server's read-side reflects the PUT.
  const recentlyMutatedIdsRef = useRef<Set<string>>(new Set());
  // The shield answers "did this row leave the list I am looking at?", so it is
  // only valid for ONE view. The archive toggle flips `archived` IN PLACE — no
  // remount — so a row shielded on the open tab was then filtered out of the
  // archive while the server-sourced total went on counting it. Nothing ever
  // cleared the set otherwise, not on refetch and not on tab return.
  useEffect(() => {
    recentlyMutatedIdsRef.current.clear();
  }, [archived]);

  // Refetch deduped by REQUEST SIGNATURE (useSignatureEffect): fires only when
  // the request below would differ from what the host prefetched into
  // `initialCompanions`. Replaces the boolean first-run guard (StrictMode
  // replay leak) and the raw `allFields` dep (identity churn). View-fields
  // stay out of the triggers on purpose (fixed projection — byte-identical
  // requests).
  const requestSignature = JSON.stringify({
    eventId,
    archived,
    page,
    pageSize,
    search,
    filters: parseFiltersApiRequest(activeFilterState.filter, allFieldsUnfiltered),
  });
  signatureRef.current = requestSignature;
  if (mountSignatureRef.current === null) mountSignatureRef.current = requestSignature;
  // Monotonic request sequence: rapid signature changes (e.g. a blur-commit
  // immediately followed by the clear button) can resolve out of order — only
  // the latest request may commit its rows or its error.
  const requestSeqRef = useRef(0);
  // The fetch lives on a ref rather than only inside the signature effect: a
  // reconcile has to be able to re-run it with the SAME params, and
  // `useSignatureEffect` no-ops on an unchanged signature by design. Same shape
  // as `GuestsProvider`'s `runFetchRef`. Reassigned every render so it always
  // closes over current props.
  const runFetchRef = useRef<() => void>(() => {});
  runFetchRef.current = () => {
    const seq = ++requestSeqRef.current;
    startTransition(async () => {
      try {
        const registrantsData = await getOrderedCompanionsWithGuests(
          eventId,
          archived,
          page - 1,
          pageSize,
          parseFiltersApiRequest(activeFilterState.filter, allFieldsUnfiltered),
          search,
        );

        if (
          "isServerError" in registrantsData &&
          registrantsData.isServerError === true
        )
          throwServerError(registrantsData);

        if (seq !== requestSeqRef.current) return;
        const fresh = (registrantsData as Guest[]).filter(
          (g) => !recentlyMutatedIdsRef.current.has(g.guestId!),
        );
        setCompanions(fresh);
      } catch (error) {
        if (seq !== requestSeqRef.current) return;
        setCompanions([]);
        setErrors((error as Error).message);
      }
    });
  };
  useSignatureEffect(requestSignature, () => {
    runFetchRef.current();
  });

  // An automation can change a guest this tab renders AFTER the response that
  // triggered it — most often the MAIN GUEST of a companion someone just
  // approved, via a `targetScope: "mainGuest"` action. Nothing else on this
  // page re-reads: the row invalidations deliberately use `refetchType: "none"`
  // (they exist to stop a remount resurrecting an actioned row, not to drive
  // live updates), and the tab has no background poll of its own.
  useEffect(() => {
    const onReconciled = (evt: Event) => {
      const detail = (evt as CustomEvent<GuestReconciledDetail>).detail;
      if (detail.eventId !== eventId) return;
      setCompanions((prev) => {
        let touched = false;
        const next = prev.map((row) => {
          if (row.guestId === detail.guestId) {
            touched = true;
            return applyServerTruth(row, detail.guest);
          }
          // The same guest can appear as this row's main guest or as one of its
          // siblings — both are rendered by the Verknüpfung cell.
          let updated = row;
          if (row.mainGuestDTO?.guestId === detail.guestId) {
            touched = true;
            updated = {
              ...updated,
              mainGuestDTO: applyServerTruth(row.mainGuestDTO, detail.guest),
            };
          }
          if (row.companions?.some((c) => c.guestId === detail.guestId)) {
            touched = true;
            updated = {
              ...updated,
              companions: updated.companions?.map((c) =>
                c.guestId === detail.guestId ? applyServerTruth(c, detail.guest) : c,
              ),
            };
          }
          return updated;
        });
        return touched ? next : prev;
      });
      // Write-through: the cache must never lag local state. A reconciled value
      // that lived only in local state was silently reverted by the next cache
      // write's seed adopt — and died with the tab switch either way.
      applyServerTruthInRequestsCache(queryClient, eventId, detail.guestId, detail.guest);
    };
    const onCoarse = (evt: Event) => {
      const detail = (evt as CustomEvent<{ eventId: string }>).detail;
      if (detail.eventId !== eventId) return;
      if (signatureRef.current === mountSignatureRef.current) {
        // Un-diverged: refresh the SNAPSHOT and let the seed adopt carry it
        // into the grid, so the cache and the rows stay one truth. The adopt
        // still filters through the recentlyMutated shield.
        void refetchRequestsSnapshots(queryClient, eventId);
        return;
      }
      // Diverged view (paged/searched/refiltered): the host snapshot is page 1
      // and cannot describe what is on screen, so this view refreshes itself.
      // Goes through runFetchRef, so the recentlyMutated shield and the
      // out-of-order guard still apply — a re-pull cannot resurrect a row the
      // user just actioned.
      runFetchRef.current();
    };
    window.addEventListener("guestReconciled", onReconciled);
    window.addEventListener("guestReconcileCoarse", onCoarse);
    return () => {
      window.removeEventListener("guestReconciled", onReconciled);
      window.removeEventListener("guestReconcileCoarse", onCoarse);
    };
  }, [eventId]);

  // An in-popup approve/decline (AcceptanceActions inside the guest-detail
  // popup) resolves a request OUTSIDE this provider — mirror the TCCAction
  // bookkeeping here so the row leaves and the tab badge/total drop without a
  // reload. The popup already invalidated the request caches.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (
        e as CustomEvent<{
          eventId?: string;
          guestId?: string;
          state?: AcceptanceState;
        }>
      ).detail;
      if (!detail?.guestId || !detail.state || detail.eventId !== eventId)
        return;
      if (!companions.some((c) => c.guestId === detail.guestId)) return;
      // A reopen makes the row leave the ARCHIVE — the mirror image of a
      // decision leaving the open tab. Only then may it be shielded and counted.
      const reopened = detail.state === AcceptanceState.PENDING;
      if (archived === reopened) recentlyMutatedIdsRef.current.add(detail.guestId);
      if (archived && reopened) {
        setCompanions((prev) => prev.filter((g) => g.guestId !== detail.guestId));
        setTotalCount((prev) => prev - 1);
        setCounts((prev) => ({ ...prev, guest: prev.guest + 1 }));
      } else if (archived) {
        setCompanions((prev) =>
          prev.map((c) =>
            c.guestId === detail.guestId
              ? { ...c, acceptanceState: detail.state }
              : c,
          ),
        );
      } else {
        setCompanions((prev) =>
          prev.filter((c) => c.guestId !== detail.guestId),
        );
        setTotalCount((prev) => prev - 1);
        setCounts((prev) => ({ ...prev, guest: prev.guest - 1 }));
      }
    };
    window.addEventListener("guestAcceptanceResolved", handler);
    return () => window.removeEventListener("guestAcceptanceResolved", handler);
  }, [eventId, archived, companions, setTotalCount, setCounts]);

  // A field save in the guest-detail popup (its own isolated GuestsProvider)
  // broadcasts the confirmed patch — merge it into the matching row so the
  // table reflects the edit without a reload. Rows also carry the linked MAIN
  // guest (row.companions / row.mainGuestDTO → Verknüpfung cell), and the
  // popup stack can edit that guest too, so merge into those spots as well.
  // Data-only: resolution (row drop, counts, refetch shield) stays with
  // guestAcceptanceResolved above.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<GuestDataUpdatedDetail>).detail;
      if (!detail?.guestId || !detail.patch || detail.eventId !== eventId)
        return;
      setCompanions((prev) =>
        prev.map((row) => {
          let next =
            row.guestId === detail.guestId
              ? mergeGuestPatch(row, detail.patch)
              : row;
          if (next.companions?.some((m) => m.guestId === detail.guestId)) {
            next = {
              ...next,
              companions: next.companions.map((m) =>
                m.guestId === detail.guestId
                  ? mergeGuestPatch(m, detail.patch)
                  : m,
              ),
            };
          }
          if (next.mainGuestDTO?.guestId === detail.guestId) {
            next = {
              ...next,
              mainGuestDTO: mergeGuestPatch(next.mainGuestDTO, detail.patch),
            };
          }
          return next;
        }),
      );
    };
    window.addEventListener("guestDataUpdated", handler);
    return () => window.removeEventListener("guestDataUpdated", handler);
  }, [eventId]);

  const createCompanion = useCallback(async (guest: Guest) => {
    // The picker captures the selected main guest as `mainGuestDTO` for
    // display only. Strip it from the API payload (it must not leak to the
    // backend — see PCreateGuest.CreationFieldsRef) and keep it locally to
    // render the optimistic row's Verknüpfung cell without a refetch.
    const { mainGuestDTO: pickedMainGuest, ...payload } = guest;
    try {
      const response = await createCompanionRequest(eventId, payload as Guest);

      if (
        typeof response === "object" &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        throwServerError(response);
      }

      // P3 (UX9): backend now returns {guestId, guest: GuestDTO}.
      // Prefer the server-hydrated DTO over the form-values guest so
      // system-computed columns (mainGuestDTO, createdAt, auto-linkage)
      // render correctly on the optimistic insert.
      if (response && typeof response === "object" && "guestId" in response) {
        const hydrated = (response as { guest?: Guest }).guest;
        const rowToInsert: Guest = hydrated
          ? { ...hydrated, guestId: (response as { guestId: string }).guestId }
          : { ...payload, guestId: (response as { guestId: string }).guestId };
        // Verknüpfung column (CompanionsRows → TCCCompanions) renders the
        // linked main guest from `companions`. When the server response
        // didn't hydrate it, fall back to the main guest the picker captured
        // so the cell isn't blank until the next refetch.
        if (
          (!rowToInsert.companions || rowToInsert.companions.length === 0) &&
          pickedMainGuest
        ) {
          rowToInsert.companions = [pickedMainGuest];
        }
        // The badge counts OPEN requests, so a new one always bumps it — even
        // while the admin is looking at the archive. The visible list and its
        // total, though, belong to whichever view is on screen: inserting a
        // fresh PENDING row into the ARCHIVE put a request into a list defined
        // as "already decided", where it also rendered with the wrong chip.
        setCounts((prev) => ({ ...prev, guest: prev.guest + 1 }));
        if (!archived) {
          setTotalCount((prev) => prev + 1);
          setCompanions((prev) => [rowToInsert, ...prev]);
        }
        // Write the row THROUGH to the host's cached snapshot: this provider is
        // unmounted by a tab switch, and that snapshot is what re-seeds it.
        // NOT for a waitlisted create — see isPendingRequestRow.
        if (isPendingRequestRow(rowToInsert)) {
          addGuestToRequestsCache(queryClient, eventId, "companions", rowToInsert);
        }
        invalidateRequestCaches();
      }
    } catch (error) {
      // To the create dialog, which renders the reason (per-field violations
      // inline) and stays open — a toast here let `await onCreation()` resolve
      // and the dialog closed on a refusal (M-11). Same as GuestsProvider.addGuest.
      const message = (error as Error).message;
      setErrors(message);
      throw error;
    }
  }, [archived, eventId, setTotalCount, setCounts, invalidateRequestCaches, queryClient]);

  /**
   * Approving a companion fires COMPANION_APPROVED, whose triggering guest is
   * the COMPANION — so a rule with `targetScope: "mainGuest"` writes the main
   * guest, and one with `"companions"` writes its siblings. Those rows stay on
   * screen here while the actioned row does not, which is exactly the case that
   * looked like a dead trigger.
   *
   * The related ids come out of the rollback snapshot this provider already
   * takes — no extra lookup, no extra request.
   *
   * Two corrections over the original: the server also fires GUEST_UPDATED for
   * this write, which was never in the derived set, so a GUEST_UPDATED rule ran
   * and nothing was armed; and "this tab has already removed it" only holds on
   * the OPEN tab — in the archive a re-decided row stays on screen, so it has to
   * be watched like any other row that can be rewritten underneath the user.
   */
  const armRelatedReconcile = useCallback(
    (
      snapshot: Guest[],
      guestId: string,
      next: AcceptanceState,
      retriggered: boolean,
      /**
       * The request carried `forceWaitlist`, so the server may rewrite `state`
       * to WAITLISTED. Only the caller knows; `next` cannot say, because it is
       * an AcceptanceState and the rewrite lands on a different column.
       */
      ownWrite: { forceWaitlist?: boolean } = {},
    ) => {
      const row = snapshot.find((c) => c.guestId === guestId);
      if (!row) return;
      const triggers = new Set(
        deriveLifecycleTriggers(
          { acceptanceState: row.acceptanceState, guestType: row.guestType },
          { acceptanceState: next },
          retriggered,
        ),
      );
      // See the twin in RegistrantsProvider: the server fires GUEST_UPDATED for
      // every write from this page, reopens included, so suppressing it here
      // left the set empty and armed nothing.
      const explicit = explicitUpdateTrigger({});
      if (explicit) triggers.add(explicit);
      const triggerList = [...triggers];
      if (!mayRun(triggerList)) return;

      const rowStays = archived && next !== AcceptanceState.PENDING;
      const relatedIds = [
        row.mainGuestDTO?.guestId,
        ...(row.companions ?? []).map((c) => c.guestId),
      ].filter((id): id is string => !!id && id !== guestId);
      // No early bail on "no related ids": the twin falls through to the
      // watches.size === 0 check and pulses coarsely instead. Returning here
      // meant a companion with no siblings and no mainGuestDTO armed nothing,
      // even with a live UPDATE_GUEST rule.
      if (relatedIds.length === 0 && !rowStays) {
        armCoarseReconcile(eventId);
        return;
      }

      // Watch the fields the RULES target — a rule writing `company` would
      // never satisfy a fixed state/acceptanceState watch. No predicted values:
      // these guests are not the ones we wrote, and `state` in particular is
      // decided server-side by the capacity CAS.
      // Drop what this write may itself move. `isSatisfied` is a `some`, so
      // one watch flipping on our own change ends the wave for this guest and
      // takes every correct sibling watch down with it.
      const fields = dropSelfWrittenFields(watchedFieldsFor(triggerList), ownWrite);
      if (fields.length === 0) {
        // Workflow list not loaded yet — pulse coarsely rather than start a
        // watch that could never satisfy.
        armCoarseReconcile(eventId);
        return;
      }
      // The related rows here are PROJECTED DTOs — `companionsList` and
      // `mainGuestDTO` are built with only `_id`, `firstName` and `lastName`
      // (GuestRepository). A watch on any other field starts with
      // `fromRaw === undefined` and can never decide the value changed, so the
      // scheduler polls to its 45s deadline and gives up silently. Same filter
      // as the twin in RegistrantsProvider.
      const relatedFields = fields.filter(
        ({ field, isAdditional }) => !isAdditional && COMPANION_DTO_FIELDS.has(field),
      );
      const watches = new Map<string, FieldWatch[]>();
      for (const id of relatedFields.length === 0 ? [] : relatedIds) {
        const known =
          row.mainGuestDTO?.guestId === id
            ? row.mainGuestDTO
            : row.companions?.find((c) => c.guestId === id);
        watches.set(
          id,
          relatedFields.map(({ field, isAdditional }) => ({
            field,
            isAdditional,
            fromRaw: (known as unknown as Record<string, unknown> | undefined)?.[field],
          })),
        );
      }
      // The re-decided row itself, whenever it stays on screen. Baseline is the
      // value we just WROTE — reading the stored one would let the first poll
      // satisfy on our own write and end the wave before the automation runs.
      if (rowStays) {
        watches.set(guestId, fields.map(({ field, isAdditional }) => ({
          field,
          isAdditional,
          // See the twin in RegistrantsProvider: the branch that used to sit
          // here never ran, so `state` kept a baseline our own write moves.
          fromRaw: isAdditional
            ? (row.additionalFields as Record<string, unknown> | undefined)?.[field]
            : (row as unknown as Record<string, unknown>)[field],
        })));
      }
      if (watches.size === 0) {
        armCoarseReconcile(eventId);
        return;
      }
      armGuestReconcile({ eventId, watches });
    },
    [eventId, archived, mayRun, watchedFieldsFor],
  );

  /**
   * Twin of RegistrantsProvider.decide — same rules, `counts.guest` and the
   * "companions" cache slot. See that file for why `leavesList` is the whole
   * bookkeeping question and why shielding an archive re-decision was wrong.
   */
  const decide = useCallback(
    async (
      guestId: string,
      next: AcceptanceState,
      opts?: { retriggerAutomations?: boolean },
    ): Promise<DecideOutcome> => {
      if (next === AcceptanceState.SYSTEM) return null;
      const reopen = next === AcceptanceState.PENDING;
      if (reopen && !archived) return null;

      const snapshot = rowsRef.current;
      const index = snapshot.findIndex((c) => c.guestId === guestId);
      const previousRow = index >= 0 ? snapshot[index] : undefined;
      if (!previousRow) return null;
      if (previousRow.acceptanceState === next) return null;

      const leavesList = archived ? reopen : !reopen;

      if (leavesList) {
        setTotalCount((prev) => prev - 1);
        setCompanions((prev) => prev.filter((c) => c.guestId !== guestId));
        recentlyMutatedIdsRef.current.add(guestId);
      }

      const rollback = () => {
        if (!leavesList) return;
        setCompanions((prev) => restoreRow(prev, previousRow, index));
        setTotalCount((prev) => prev + 1);
        recentlyMutatedIdsRef.current.delete(guestId);
      };

      try {
        const payload: Guest = { guestId, acceptanceState: next };
        if (opts?.retriggerAutomations) payload.retriggerAutomations = true;
        const response = await updateGuests(eventId, [payload]);

        if (isServerErrorResponse(response)) {
          // The capacity gate is guest-type AGNOSTIC server-side
          // (`countsAgainstContingent` reads acceptanceState + state, nothing
          // else), so a companion re-approval at a full event raises the same
          // 409 a registrant's does. This branch used to be missing here, and
          // the whole difference was visible to the admin: registrants got the
          // 3-option popup and kept their opt-in, companions got a red toast, a
          // rolled-back row, and no way to proceed at all.
          const ctx = capacityContextOf(response);
          if (ctx && approvalBlockedGuestIds(response).includes(guestId)) {
            rollback();
            setDecisionError(null);
            setCapacityDecision({
              guestId,
              context: ctx,
              retriggerAutomations: opts?.retriggerAutomations === true,
            });
            // Not a success — the modal, not a toast, carries the next step.
            return null;
          }
          throwServerError(response);
        }

        if (leavesList) {
          if (reopen) {
            setCounts((prev) => ({ ...prev, guest: prev.guest + 1 }));
            addGuestToRequestsCache(queryClient, eventId, "companions", {
              ...previousRow,
              acceptanceState: AcceptanceState.PENDING,
            });
          } else {
            setCounts((prev) => ({ ...prev, guest: prev.guest - 1 }));
            dropGuestFromRequestsCache(queryClient, eventId, guestId, "companions");
          }
        } else {
          setCompanions((prev) =>
            prev.map((c) => (c.guestId === guestId ? { ...c, acceptanceState: next } : c)),
          );
        }
        invalidateRequestCaches();
        armRelatedReconcile(snapshot, guestId, next, opts?.retriggerAutomations === true);
        return { triggers: enqueuedTriggersFor(response, guestId) };
      } catch (error) {
        rollback();
        const message = (error as Error).message;
        setErrors(message);
        surfaceError(error);
        return null;
      }
    },
    [
      eventId,
      archived,
      setTotalCount,
      setCounts,
      surfaceError,
      invalidateRequestCaches,
      armRelatedReconcile,
      queryClient,
    ],
  );

  /**
   * The second half of the capacity decision: the admin picked "waitlist" or
   * "add anyway" in the modal, so re-send with the flag that says which.
   *
   * Mirrors RegistrantsProvider.resubmitAccept deliberately and literally — the
   * two lanes are twins and the point of keeping them side by side is that a
   * reviewer can diff them. Four things here are load-bearing rather than
   * stylistic:
   *
   *  1. The waitlist arm must NOT carry the opt-in. "Auf Warteliste" approves
   *     nobody; the server rewrites the write to SYSTEM/WAITLISTED and fires
   *     nothing, so passing it would promise an email that cannot be sent.
   *  2. The `!archived` guards. In the archive the row STAYS and is re-mapped
   *     in place; shielding or dropping it there makes it vanish on the next
   *     refetch.
   *  3. `armRelatedReconcile` gets the same opt-in the payload carried.
   *     Passing `false` blinds the cockpit to exactly the automation the admin
   *     just paid for.
   *  4. The snapshot comes from the `companions` state value, not from
   *     `rowsRef` as in `decide`. That asymmetry is what rollback restores;
   *     "tidying" it changes what comes back.
   */
  const resubmitAccept = useCallback(
    async (guestId: string, decision: "waitlist" | "normal", retrigger = false) => {
      setDecisionLoading(true);
      const snapshot = companions;
      if (!archived) {
        setTotalCount((prev) => prev - 1);
        setCompanions((prev) => prev.filter((c) => c.guestId !== guestId));
        recentlyMutatedIdsRef.current.add(guestId);
      }
      try {
        const payload: Guest =
          decision === "waitlist"
            ? { guestId, acceptanceState: AcceptanceState.ACCEPTED, forceWaitlist: true }
            : { guestId, acceptanceState: AcceptanceState.ACCEPTED, overrideRules: true };
        if (decision === "normal" && retrigger) payload.retriggerAutomations = true;
        const response = await updateGuests(eventId, [payload]);
        if (isServerErrorResponse(response)) {
          // No capacity arm here on purpose: this IS the answer to the capacity
          // question. A second 409 means the event filled up again between the
          // popup and the click, and re-opening the same modal would loop.
          throwServerError(response);
        }
        // Went through: only now does the popup close (R3).
        setCapacityDecision(null);
        if (archived) {
          setCompanions((prev) =>
            prev.map((c) =>
              c.guestId === guestId
                ? {
                    ...c,
                    // "normal" (overrideRules) is deterministic: the server
                    // admits over the cap and stores ACCEPTED. "waitlist" is
                    // NOT — the reroute only fires while the capacity gate
                    // would still block, and capacity can free up between the
                    // dialog rendering and the click. Painting SYSTEM there
                    // put a waitlist badge on an accepted guest, against this
                    // file's own invariant that waitlist membership is
                    // server-owned. Leave the ambiguous branch alone and let
                    // the read-back below replace it with what was stored.
                    acceptanceState:
                      decision === "waitlist"
                        ? c.acceptanceState
                        : AcceptanceState.ACCEPTED,
                  }
                : c,
            ),
          );
        }
        if (!archived) {
          // The badge counts OPEN requests, and this row just left them.
          // Without this it keeps counting the guest until a reload.
          setCounts((prev) => ({ ...prev, guest: prev.guest - 1 }));
          dropGuestFromRequestsCache(queryClient, eventId, guestId, "companions");
        }
        invalidateRequestCaches();
        armRelatedReconcile(
          snapshot, guestId, AcceptanceState.ACCEPTED,
          decision === "normal" && retrigger,
          { forceWaitlist: decision === "waitlist" },
        );
        // Only the ambiguous branch, and only when the row is still on screen.
        // `invalidateRequestCaches` uses `refetchType: "none"`, so without this
        // read nothing re-reads the row and the pre-write badge would simply
        // stay. One request, in exchange for never showing a verdict the
        // server did not give.
        if (archived && decision === "waitlist") {
          void readBackGuestVerdict(eventId, guestId).then((stored) => {
            if (!stored) return;
            setCompanions((prev) =>
              prev.map((g) =>
                g.guestId === guestId
                  ? {
                      ...g,
                      acceptanceState: stored.acceptanceState,
                      // BOTH columns. The reroute moves `state` too, and
                      // applying only the acceptance half left the status cell
                      // showing the pre-write value next to a corrected badge.
                      ...(stored.state !== null ? { state: stored.state as typeof g.state } : {}),
                    }
                  : g,
              ),
            );
          });
        }
        // The write went through and may well have mailed. Saying nothing here
        // is the same silence the whole dialog exists to remove — it just moved
        // one modal further in.
        window.dispatchEvent(
          new CustomEvent("guestActionToast", {
            detail: {
              kind: archived ? "redecided" : "approved",
              retriggered: decision === "normal" && retrigger,
              triggers: enqueuedTriggersFor(response, guestId),
            },
          }),
        );
      } catch (error) {
        setCompanions(snapshot);
        if (!archived) setTotalCount((prev) => prev + 1);
        recentlyMutatedIdsRef.current.delete(guestId);
        const message = (error as Error).message;
        setErrors(message);
        // In the popup, which stays open — no second channel (M-10).
        setDecisionError(error);
      } finally {
        setDecisionLoading(false);
      }
    },
    [
      eventId,
      archived,
      companions,
      setTotalCount,
      setCounts,
      invalidateRequestCaches,
      queryClient,
      armRelatedReconcile,
    ],
  );

  const acceptCompanion = useCallback(
    (guestId: string) => decide(guestId, AcceptanceState.ACCEPTED),
    [decide],
  );

  const declineCompanion = useCallback(
    (guestId: string) => decide(guestId, AcceptanceState.DECLINED),
    [decide],
  );

  const contextValue = useMemo(() => ({
    errors, setErrors, companions, accept: acceptCompanion, decline: declineCompanion, decide,
    setCompanions, createCompanion, loading: isPending,
  }), [errors, companions, acceptCompanion, declineCompanion, decide, createCompanion, isPending]);

  return (
    <CompanionsContext.Provider value={contextValue}>
      {children}
      {capacityDecision && (
        <CapacityDecisionModal
          context={capacityDecision.context}
          kind="accept"
          isLoading={decisionLoading}
          onWaitlist={() => resubmitAccept(capacityDecision.guestId, "waitlist")}
          onNormal={() =>
            resubmitAccept(
              capacityDecision.guestId,
              "normal",
              capacityDecision.retriggerAutomations === true,
            )
          }
          onCancel={() => setCapacityDecision(null)}
          error={decisionError ?? undefined}
        />
      )}
    </CompanionsContext.Provider>
  );
};

export const useCompanions = () => {
  const context = useContext(CompanionsContext);
  return context;
};
